import { PureComponent, ReactElement } from 'react'

export interface ChildProps {
  /**
   * Whether or not all rows have been selected
   */
  readonly allSelected: boolean

  /**
   * If true, then all rows are implicitly selected. This value is
   * useful for determining if there could be unloaded rows that are
   * considered selected.
   */
  readonly invertedSelection: boolean

  readonly isRowSelected: (id: string) => boolean
  readonly selectedCount: number

  readonly onSelectRow: (id: string) => void
  readonly onSelectAllRows: () => void
  readonly selectionDataValue: unknown
  readonly onClearSelection: () => void
}

interface Props {
  readonly rowCount: number
  readonly children: (props: ChildProps) => ReactElement<any>

  /**
   * The types for these remaining props are intentionally
   * opaque. They are used solely to determine whether
   * or not the selection should be reset via comparison with
   * the previous value.
   */
  readonly order: unknown
  readonly filterText: unknown
  readonly quickFilterID: unknown

  readonly getAllCount?: (props: { readonly toggled: number }) => number
}

interface State {
  /**
   * The selected rows, or if allSelected == true, the deselected rows
   */
  readonly toggledRows: {
    readonly [id: string]: boolean
  }
  readonly allSelected: boolean
}

export class SelectionManager extends PureComponent<Props, State> {
  public readonly state: State = {
    toggledRows: {},
    allSelected: false
  }

  public render() {
    const { children } = this.props
    const { allSelected } = this.state
    return children({
      allSelected: allSelected && this.getToggledCount() === 0,
      invertedSelection: allSelected,
      isRowSelected: this.isRowSelected,
      selectedCount: this.getSelectedCount(),
      onSelectRow: this.handleSelectRow,
      onSelectAllRows: this.handleSelectAllRows,
      selectionDataValue: this.state.toggledRows,
      onClearSelection: this.handleClearSelection
    })
  }

  public componentDidUpdate(prevProps: Props) {
    const {
      order: previousOrder,
      filterText: previousFilterText,
      quickFilterID: previousQuickFilterID,
      rowCount: previousRowCount
    } = prevProps
    const { order, filterText, quickFilterID, rowCount } = this.props

    if (
      (order !== previousOrder ||
        filterText !== previousFilterText ||
        quickFilterID !== previousQuickFilterID ||
        previousRowCount !== rowCount) &&
      this.getToggledCount() > 0
    ) {
      this.setState({ toggledRows: {}, allSelected: false })
    }
  }

  private isRowSelected = (id: string) => {
    return this.state.allSelected
      ? !this.state.toggledRows[id]
      : this.state.toggledRows[id]
  }

  private getSelectedCount = () => {
    const { allSelected } = this.state
    const { rowCount, getAllCount } = this.props
    const toggled = this.getToggledCount()

    const allCount = getAllCount ? getAllCount({ toggled }) : rowCount - toggled

    return allSelected ? allCount : toggled
  }

  private getToggledCount = () => {
    const { toggledRows } = this.state

    return Object.keys(toggledRows).reduce(
      (acc, key) => (toggledRows[key] ? acc + 1 : acc),
      0
    )
  }

  private handleSelectRow = (toggledID: string) => {
    this.setState(({ toggledRows: prevToggledRows }) => {
      const toggledRows = {
        ...prevToggledRows,
        [toggledID]: !prevToggledRows[toggledID]
      }

      return {
        toggledRows
      }
    })
  }

  private handleSelectAllRows = () => {
    this.setState({
      toggledRows: {},
      allSelected: !this.state.allSelected || this.getToggledCount() > 0
    })
  }

  private handleClearSelection = () => {
    this.setState({
      toggledRows: {},
      allSelected: false
    })
  }
}
